import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../api/queryClient'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useFormatters } from '../lib/format'
import { SECTION_ICONS } from '../lib/section-icons'
import { AssetPicker } from './AssetPicker'
import { LeaseVerdictBadge, useLeaseRegimes, useRentLabel } from './LeaseVerdictBadge'
import { PageHeader } from './shared/PageHeader'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { EmptyState } from './ui/empty-state'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Skeleton } from './ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import type { LeaseStatusFilter } from '../routes/$org/leases/index'

const PER_PAGE = 50
const ALL_REGIMES = '__all__'

type Search = { under: string | undefined; regime: string | undefined; ends_before: string | undefined; status: LeaseStatusFilter | undefined; page: number | undefined }

/**
 * The organization's leases: which lots are let, to whom, at what rent, until
 * when (spec 2026-10-25-03 §3). Filters live in the URL so the steering
 * console can link to a perimeter.
 */
export function LeasesPage() {
    const { org } = useParams({ from: '/$org/leases/' })
    const search: Search = useSearch({ from: '/$org/leases/' })
    const navigate = useNavigate()
    const { t } = useTranslation('leases')
    const { formatDate } = useFormatters()
    const rentLabel = useRentLabel()
    useBreadcrumbs([{ title: t('common:nav.home'), path: `/${org}` }, { title: t('title') }])

    const setSearch = (patch: Partial<Search>) =>
        navigate({ to: '/$org/leases', params: { org }, search: { ...search, page: undefined, ...patch }, replace: true })

    const { regimes, regimeLabel } = useLeaseRegimes(org)

    const page = search.page ?? 1
    const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/leases', {
        params: {
            path: { orgCode: org },
            query: {
                under: search.under,
                regime: search.regime,
                ends_before: search.ends_before,
                status: search.status,
                page,
                limit: PER_PAGE,
            },
        },
    })
    const rows = data?.data ?? []
    const totalPages = data?.total_pages ?? 1

    const { data: underAsset } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}',
        { params: { path: { orgCode: org, assetUid: search.under ?? '' } } },
        { enabled: !!search.under },
    )
    const filtered = !!(search.under || search.regime || search.ends_before || search.status)

    return (
        <div className="space-y-6" data-testid="leases-page">
            <PageHeader icon={SECTION_ICONS.leases} title={t('title')} description={t('description')} />

            <div className="grid gap-3 md:grid-cols-4" data-testid="leases-filters">
                <div>
                    <Label className="mb-1 block text-sm">{t('filter.under')}</Label>
                    <AssetPicker
                        org={org}
                        value={search.under}
                        valueLabel={(underAsset as { name?: string } | undefined)?.name}
                        testId="leases-filter-under"
                        onChange={(uid) => setSearch({ under: uid })}
                    />
                </div>
                <div>
                    <Label className="mb-1 block text-sm">{t('filter.regime')}</Label>
                    <Select value={search.regime ?? ALL_REGIMES} onValueChange={(v) => setSearch({ regime: v === ALL_REGIMES ? undefined : v })}>
                        <SelectTrigger data-testid="leases-filter-regime">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL_REGIMES} data-testid="leases-filter-regime-all">{t('filter.regime_all')}</SelectItem>
                            {regimes.map((r) => (
                                <SelectItem key={r.code} value={r.code} data-testid={`leases-filter-regime-${r.code}`}>
                                    {r.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="mb-1 block text-sm" htmlFor="leases-ends-before">{t('filter.ends_before')}</Label>
                    <Input
                        id="leases-ends-before"
                        type="date"
                        value={search.ends_before ?? ''}
                        onChange={(e) => setSearch({ ends_before: e.target.value || undefined })}
                        data-testid="leases-filter-ends-before"
                    />
                </div>
                <div>
                    <Label className="mb-1 block text-sm">{t('filter.status')}</Label>
                    <Select value={search.status ?? 'running'} onValueChange={(v) => setSearch({ status: v === 'running' ? undefined : (v as LeaseStatusFilter) })}>
                        <SelectTrigger data-testid="leases-filter-status">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {(['running', 'draft', 'all'] as const).map((s) => (
                                <SelectItem key={s} value={s} data-testid={`leases-filter-status-${s}`}>
                                    {t(`status.${s}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            {filtered && (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSearch({ under: undefined, regime: undefined, ends_before: undefined, status: undefined })}
                    data-testid="leases-filter-clear"
                >
                    {t('filter.clear')}
                </Button>
            )}

            {isLoading ? (
                <Skeleton className="h-40 w-full" data-testid="leases-loading" />
            ) : rows.length === 0 ? (
                <EmptyState icon={SECTION_ICONS.leases} title={t('empty.title')} description={t('empty.description')} data-testid="leases-empty" />
            ) : (
                <Card>
                    <CardContent className="overflow-x-auto p-0">
                        <Table data-testid="leases-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('column.lots')}</TableHead>
                                    <TableHead>{t('column.tenant')}</TableHead>
                                    <TableHead>{t('column.regime')}</TableHead>
                                    <TableHead className="text-right">{t('column.rent')}</TableHead>
                                    <TableHead>{t('column.term')}</TableHead>
                                    <TableHead>{t('column.verdict')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow
                                        key={row.uid}
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() => navigate({ to: '/$org/contracts/$contractUid', params: { org, contractUid: row.uid } })}
                                        data-testid="lease-row"
                                        data-lease-uid={row.uid}
                                    >
                                        <TableCell className="font-medium">
                                            <Link
                                                to="/$org/contracts/$contractUid"
                                                params={{ org, contractUid: row.uid }}
                                                className="hover:underline"
                                                data-testid="lease-row-link"
                                            >
                                                {row.lots.map((l) => l.name).join(', ') || row.name || '—'}
                                            </Link>
                                        </TableCell>
                                        <TableCell data-testid="lease-row-tenant">{row.tenants.join(', ') || t('no_tenant')}</TableCell>
                                        <TableCell data-testid="lease-row-regime">{regimeLabel(row.regime_code)}</TableCell>
                                        <TableCell className="text-right tabular-nums" data-testid="lease-row-rent">
                                            {rentLabel(row.rent_amount_minor, row.rent_currency, row.rent_frequency)}
                                        </TableCell>
                                        <TableCell data-testid="lease-row-term">
                                            {row.term ? formatDate(row.term) : t('open_ended')}
                                            {row.ended_on && <span className="block text-xs text-muted-foreground">{t('ended_on', { date: formatDate(row.ended_on) })}</span>}
                                        </TableCell>
                                        <TableCell>
                                            <LeaseVerdictBadge verdict={row.verdict} testId="lease-row-verdict" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {totalPages > 1 && (
                <div className="flex items-center justify-end gap-2 text-sm" data-testid="leases-pagination">
                    <span className="text-muted-foreground">{t('pagination', { page, pages: totalPages })}</span>
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setSearch({ page: page - 1 > 1 ? page - 1 : undefined })} data-testid="leases-previous">
                        {t('previous')}
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setSearch({ page: page + 1 })} data-testid="leases-next">
                        {t('next')}
                    </Button>
                </div>
            )}
        </div>
    )
}
